package horcrux.frontend.common.clients.backendclient

import arrow.core.Either
import arrow.core.NonEmptyList
import arrow.core.raise.either
import horcrux.common.HorcruxEither
import horcrux.common.ServerAppRoute
import horcrux.common.api.dtos.ChatMessage
import horcrux.common.api.dtos.ChatPageID
import horcrux.common.api.dtos.CollectionPrivacyType
import horcrux.common.api.dtos.DeleteChatPageRequest
import horcrux.common.api.dtos.DeleteChatPageResponse
import horcrux.common.api.dtos.DeleteCollectionRequest
import horcrux.common.api.dtos.DeleteDocumentRequest
import horcrux.common.api.dtos.DeleteDocumentResponse
import horcrux.common.api.dtos.EmbeddingModelID
import horcrux.common.api.dtos.GetCollectionResponse
import horcrux.common.api.dtos.GetDocumentResponse
import horcrux.common.api.dtos.PatchCollectionRequest
import horcrux.common.api.dtos.PatchCollectionResponse
import horcrux.common.api.dtos.PatchDocumentRequest
import horcrux.common.api.dtos.PatchDocumentResponse
import horcrux.common.api.dtos.PostChatPageRequest
import horcrux.common.api.dtos.PostChatPageResponse
import horcrux.common.api.dtos.PostChatPagesRequest
import horcrux.common.api.dtos.PostChatPagesResponse
import horcrux.common.api.dtos.PostCollectionRequest
import horcrux.common.api.dtos.PostCollectionResponse
import horcrux.common.api.dtos.PostCollectionsRequest
import horcrux.common.api.dtos.PostCollectionsResponse
import horcrux.common.api.dtos.PostDocumentsRequest
import horcrux.common.api.dtos.PostDocumentsRequestType
import horcrux.common.api.dtos.PostDocumentsResponse
import horcrux.common.api.dtos.PostFeedbackRequest
import horcrux.common.api.dtos.PostFeedbackResponse
import horcrux.common.api.dtos.PostQuestionRequest
import horcrux.common.api.dtos.PostQuestionResponse
import horcrux.common.api.dtos.PostRawDocumentsRequest
import horcrux.common.api.dtos.PostRawDocumentsResponse
import horcrux.common.api.dtos.PutCollectionRequest
import horcrux.common.api.dtos.PutCollectionResponse
import horcrux.common.api.dtos.RawDocumentClientSideID
import horcrux.common.api.dtos.RawDocumentIdentifier
import horcrux.common.api.dtos.types.ChatModelConfigs
import horcrux.common.api.dtos.types.CollectionID
import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.api.dtos.types.DocumentChunkID
import horcrux.common.api.dtos.types.DocumentID
import horcrux.common.api.dtos.types.DocumentIdentifier
import horcrux.common.api.dtos.types.DocumentName
import horcrux.common.api.dtos.types.EmbeddingModelConfigs
import horcrux.common.api.dtos.types.Question
import horcrux.common.api.dtos.types.UserFeedback
import horcrux.common.types.EndpointVersion
import horcrux.common.types.NonEmptyMap
import horcrux.frontend.common.clients.BackendAPIAccessToken
import horcrux.frontend.common.extensions.invoke
import horcrux.frontend.common.views.chat.ChatMessageData
import io.ktor.client.call.body
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.contentType

class AuthenticatedBackendClient(backendAPIAccessToken: BackendAPIAccessToken) : BackendClient() {
    private val client = getClient {
        install(Auth) {
            bearer {
                loadTokens {
                    BearerTokens(backendAPIAccessToken.value, backendAPIAccessToken.value)
                }
            }
        }
    }


    suspend fun importDocuments(
        collectionID: CollectionID,
        documentIdentifiers: NonEmptyList<DocumentIdentifier>,
        embeddingModelConfigs: EmbeddingModelConfigs
    ): HorcruxEither<PostDocumentsResponse> = either {
        val postDocumentsRequest = PostDocumentsRequest(
            collectionID = collectionID,
            documentIdentifiers = documentIdentifiers,
            embeddingModelConfigs = embeddingModelConfigs,
            postDocumentsRequestType = PostDocumentsRequestType.PROCESS_DOCUMENTS_AND_GENERATE_EMBEDDINGS,
        )

        submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.DOCUMENTS) {
                contentType(ContentType.Application.Json)
                setBody(postDocumentsRequest)
            }
        }.map<PostDocumentsResponse> { it.body() }.bind()
    }

    suspend fun askQuestion(
        collectionID: CollectionID,
        question: Question,
        chatModelConfigs: ChatModelConfigs,
    ): HorcruxEither<PostQuestionResponse> {
        val postQuestionRequest = PostQuestionRequest(
            collectionID = collectionID,
            question = question,
            chatModelConfigs = chatModelConfigs,
        )

        return submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.QUESTIONS) {
                contentType(ContentType.Application.Json)
                setBody(postQuestionRequest)
            }
        }.map { it.body() }
    }

    suspend fun postCollection(
        collectionName: CollectionName,
        embeddingModelID: EmbeddingModelID,
    ): HorcruxEither<PostCollectionResponse> {
        val postCollectionRequest = PostCollectionRequest(
            collectionName = collectionName,
            embeddingModelID = embeddingModelID,
        )

        return submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.COLLECTION) {
                contentType(ContentType.Application.Json)
                setBody(postCollectionRequest)
            }
        }.map { it.body() }
    }

    suspend fun getCollections(): HorcruxEither<PostCollectionsResponse> {
        return submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.COLLECTIONS) {
                contentType(ContentType.Application.Json)
                setBody(PostCollectionsRequest())
            }
        }.map { it.body() }
    }

    suspend fun renameCollection(
        collectionID: CollectionID, newCollectionName: CollectionName
    ): HorcruxEither<PatchCollectionResponse> = either {
        val patchCollectionRequest = PatchCollectionRequest(
            collectionID = collectionID, newCollectionName = newCollectionName
        )

        submitAndHandleErrors {
            client.submit(HttpMethod.Patch, ServerAppRoute.COLLECTION) {
                contentType(ContentType.Application.Json)
                setBody(patchCollectionRequest)
            }
        }.map<PatchCollectionResponse> { it.body() }.bind()
    }

    suspend fun getCollection(collectionID: CollectionID): HorcruxEither<GetCollectionResponse> = either {
        submitAndHandleErrors {
            client.submit(HttpMethod.Get, ServerAppRoute.COLLECTION) {
                stringParameter(CollectionID.PARAMETER_NAME.value, collectionID.betterToString())
            }
        }.map<GetCollectionResponse> { it.body() }.bind()
    }

    suspend fun getDocumentChunk(
        documentChunkID: DocumentChunkID,
        collectionID: CollectionID
    ): HorcruxEither<GetDocumentResponse> = submitAndHandleErrors {
        client.submit(HttpMethod.Get, ServerAppRoute.DOCUMENT) {
            stringParameter(DocumentChunkID.PARAMETER_NAME.value, documentChunkID.betterToString())
            stringParameter(CollectionID.PARAMETER_NAME.value, collectionID.betterToString())
            stringParameter(EndpointVersion.PARAMETER_NAME.value, EndpointVersion.ONE.betterToString())
        }
    }.map { it.body() }

    suspend fun renameDocument(
        documentID: DocumentID,
        collectionID: CollectionID,
        newDocumentName: DocumentName,
    ): HorcruxEither<PatchDocumentResponse> {
        val patchDocumentRequest = PatchDocumentRequest(
            documentID = documentID,
            collectionID = collectionID,
            newDocumentName = newDocumentName,
        )

        return submitAndHandleErrors {
            client.submit(HttpMethod.Patch, ServerAppRoute.DOCUMENT) {
                contentType(ContentType.Application.Json)
                setBody(patchDocumentRequest)
            }
        }.map { it.body() }
    }

    suspend fun deleteDocument(
        documentID: DocumentID,
        collectionID: CollectionID,
    ): HorcruxEither<DeleteDocumentResponse> {
        val deleteDocumentRequest = DeleteDocumentRequest(
            documentID = documentID,
            collectionID = collectionID,
        )

        return submitAndHandleErrors {
            client.submit(HttpMethod.Delete, ServerAppRoute.DOCUMENT) {
                contentType(ContentType.Application.Json)
                setBody(deleteDocumentRequest)
            }
        }.map { it.body() }
    }

    suspend fun deleteCollection(
        collectionID: CollectionID
    ): HorcruxEither<DeleteDocumentResponse> {
        val deleteCollectionRequest = DeleteCollectionRequest(
            collectionID = collectionID
        )

        return submitAndHandleErrors {
            client.submit(HttpMethod.Delete, ServerAppRoute.COLLECTION) {
                contentType(ContentType.Application.Json)
                setBody(deleteCollectionRequest)
            }
        }.map { it.body() }
    }

    suspend fun deleteChatPage(
        chatPageID: ChatPageID
    ): HorcruxEither<DeleteChatPageResponse> = DeleteChatPageRequest(
        chatPageID = chatPageID
    ).let { request ->
        submitAndHandleErrors {
            client.submit(HttpMethod.Delete, ServerAppRoute.CHAT_PAGE) {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.map { it.body() }
    }

    suspend fun submitFeedback(
        userFeedback: UserFeedback
    ): HorcruxEither<PostFeedbackResponse> = either {
        val postFeedbackRequest = PostFeedbackRequest(
            userFeedback = userFeedback,
        ).validate().bind()

        submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.FEEDBACK) {
                contentType(ContentType.Application.Json)
                setBody(postFeedbackRequest)
            }
        }.map<PostFeedbackResponse> { it.body() }.bind()
    }

    suspend fun publishCollection(
        collectionID: CollectionID,
        chatMessageDataList: List<ChatMessageData>,
        collectionNameMaybe: CollectionName?,
    ): Either<Throwable, PostChatPageResponse> = either {
        val postChatPageRequest = PostChatPageRequest(
            collectionID = collectionID,
            chatMessages = chatMessageDataList.map { ChatMessage(it) },
            collectionNameMaybe = collectionNameMaybe
        )
        submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.CHAT_PAGE) {
                contentType(ContentType.Application.Json)
                setBody(postChatPageRequest)
            }
        }.map<PostChatPageResponse> { it.body() }.bind()
    }

    suspend fun getChatPageMetadataList(): Either<Throwable, PostChatPagesResponse> = either {
        submitAndHandleErrors {
            client.submit(HttpMethod.Post, ServerAppRoute.CHAT_PAGES) {
                contentType(ContentType.Application.Json)
                setBody(PostChatPagesRequest)
            }
        }.map<PostChatPagesResponse> { it.body() }.bind()
    }

    suspend fun importCollectionToAccount(
        collectionID: CollectionID, collectionNameMaybe: CollectionName?
    ): Either<Throwable, PutCollectionResponse> = PutCollectionRequest(
        collectionID = collectionID,
        collectionPrivacyType = CollectionPrivacyType.PRIVATE,
        collectionNameMaybe = collectionNameMaybe,
    ).let { request ->
        submitAndHandleErrors {
            client.submit(HttpMethod.Put, ServerAppRoute.COLLECTION) {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.map { it.body() }
    }

    suspend fun postRawDocuments(
        rawDocumentIdentifiersByID: NonEmptyMap<RawDocumentClientSideID, RawDocumentIdentifier>
    ): Either<Throwable, PostRawDocumentsResponse> =
        PostRawDocumentsRequest(
            rawDocumentIdentifiersByID = rawDocumentIdentifiersByID
        ).let { request ->
            submitAndHandleErrors {
                client.submit(HttpMethod.Post, ServerAppRoute.RAW_DOCUMENTS) {
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }.map { it.body() }
        }
}
